import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, ArrowRight } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

function Navigation() {
  return (
    <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <Link href="/" data-testid="link-home">
          <img src="/logo.png" alt="ReviewKit" className="h-10 object-contain" />
        </Link>
        <nav className="hidden md:flex items-center gap-6">
          <Link href="/features" className="text-sm text-muted-foreground hover:text-foreground" data-testid="link-features">
            Features
          </Link>
          <Link href="/pricing" className="text-sm text-foreground font-medium" data-testid="link-pricing">
            Pricing
          </Link>
          <Link href="/docs" className="text-sm text-muted-foreground hover:text-foreground" data-testid="link-docs">
            Docs
          </Link>
          <Link href="/blog" className="text-sm text-muted-foreground hover:text-foreground" data-testid="link-blog">
            Blog
          </Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link href="/login">
            <Button variant="ghost" size="sm" data-testid="button-login">
              Login
            </Button>
          </Link>
          <Link href="/signup">
            <Button size="sm" data-testid="button-signup">
              Sign Up Free
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function PlanCard({
  name,
  price,
  period,
  description,
  features,
  buttonText,
  buttonVariant = "default",
  highlighted = false,
  onSelect,
  isPending = false,
  testId,
}: {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  buttonText: string;
  buttonVariant?: "default" | "outline";
  highlighted?: boolean;
  onSelect: () => void;
  isPending?: boolean;
  testId: string;
}) {
  return (
    <Card className={`relative ${highlighted ? "border-primary border-2" : ""}`} data-testid={testId}>
      {highlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge variant="default">MOST POPULAR</Badge>
        </div>
      )}
      <CardHeader className="text-center pt-8">
        <CardTitle className="text-xl">{name}</CardTitle>
        <div className="mt-4">
          <span className="text-4xl font-bold">{price}</span>
          <span className="text-muted-foreground ml-1">{period}</span>
        </div>
        <p className="text-sm text-muted-foreground mt-2">{description}</p>
      </CardHeader>
      <CardContent className="pt-6">
        <ul className="space-y-3 mb-8">
          {features.map((feature, i) => (
            <li key={i} className="flex items-start gap-2">
              <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <span className="text-sm">{feature}</span>
            </li>
          ))}
        </ul>
        <Button
          variant={buttonVariant}
          className="w-full"
          onClick={onSelect}
          disabled={isPending}
          data-testid={`button-select-${testId}`}
        >
          {isPending ? "Loading..." : buttonText}
        </Button>
      </CardContent>
    </Card>
  );
}

function FAQ() {
  const faqs = [
    {
      question: "What happens when I hit my limit?",
      answer: "You'll see a friendly prompt to upgrade. No surprise charges, ever.",
    },
    {
      question: "Can I upgrade my plan later?",
      answer: "Yes! You can upgrade from Free to Lifetime or Pro anytime from your dashboard settings.",
    },
    {
      question: "Is there a free trial?",
      answer: "Yes - the free plan! Use it as long as you want with 10 requests/month.",
    },
    {
      question: "What's your refund policy?",
      answer: "30-day money-back guarantee, no questions asked.",
    },
  ];

  return (
    <section className="py-16">
      <div className="max-w-3xl mx-auto px-6">
        <h2 className="text-2xl font-semibold text-center mb-8" data-testid="text-faq-title">
          Frequently Asked Questions
        </h2>
        <div className="space-y-6">
          {faqs.map((faq, i) => (
            <div key={i} data-testid={`faq-item-${i}`}>
              <h3 className="font-medium mb-2">{faq.question}</h3>
              <p className="text-muted-foreground">{faq.answer}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <img src="/logo.png" alt="ReviewKit" className="h-10 object-contain" />
          <nav className="flex flex-wrap items-center justify-center gap-6">
            <Link href="/features" className="text-sm text-muted-foreground hover:text-foreground">
              Features
            </Link>
            <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground">
              Pricing
            </Link>
            <Link href="/docs" className="text-sm text-muted-foreground hover:text-foreground">
              Docs
            </Link>
            <Link href="/blog" className="text-sm text-muted-foreground hover:text-foreground">
              Blog
            </Link>
            <Link href="/contact" className="text-sm text-muted-foreground hover:text-foreground">
              Contact
            </Link>
            <Link href="/privacy" className="text-sm text-muted-foreground hover:text-foreground">
              Privacy
            </Link>
            <Link href="/terms" className="text-sm text-muted-foreground hover:text-foreground">
              Terms
            </Link>
            <Link href="/login" className="text-sm text-muted-foreground hover:text-foreground">
              Login
            </Link>
          </nav>
          <div className="text-sm text-muted-foreground">
            {new Date().getFullYear()} ReviewKit. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
}

// Stripe price IDs from environment variables
const STRIPE_PRICES: Record<string, string | undefined> = {
  lifetime: import.meta.env.VITE_STRIPE_LIFETIME_PRICE_ID,
  pro: import.meta.env.VITE_STRIPE_PRO_PRICE_ID,
};

export default function Pricing() {
  const { toast } = useToast();
  const { session } = useAuth();

  const checkoutMutation = useMutation({
    mutationFn: async (plan: "lifetime" | "pro") => {
      const priceId = STRIPE_PRICES[plan];
      if (!priceId) {
        throw new Error("Stripe price not configured for this plan");
      }
      const res = await apiRequest("POST", "/api/stripe/create-checkout-session", {
        priceId,
        planType: plan,
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error?.message || "Failed to start checkout");
      }
      return res.json();
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to start checkout. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSelectPlan = (plan: "free" | "lifetime" | "pro") => {
    if (plan === "free") {
      window.location.href = "/signup";
    } else {
      // If user is not authenticated, redirect to signup with plan intent
      if (!session) {
        window.location.href = `/signup?plan=${plan}`;
        return;
      }
      checkoutMutation.mutate(plan);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main>
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="text-pricing-headline">
              Choose Your Plan
            </h1>
            <p className="text-lg text-muted-foreground mb-12" data-testid="text-pricing-subheadline">
              Start free. Upgrade when you're ready.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <PlanCard
                name="Free"
                price="$0"
                period="forever"
                description="Perfect for getting started"
                features={[
                  "10 review requests/month",
                  "1 staff member",
                  "QR code generator",
                  "Basic dashboard",
                ]}
                buttonText="Start Free"
                buttonVariant="outline"
                onSelect={() => handleSelectPlan("free")}
                testId="plan-free"
              />
              <PlanCard
                name="Lifetime"
                price="$79"
                period="one-time"
                description="Best value for growing businesses"
                features={[
                  "500 review requests/month",
                  "Unlimited staff members",
                  "Job photo attachments",
                  "Staff leaderboards",
                  "Priority support",
                  "All future updates",
                ]}
                buttonText="Buy Lifetime"
                highlighted
                onSelect={() => handleSelectPlan("lifetime")}
                isPending={checkoutMutation.isPending}
                testId="plan-lifetime"
              />
              <PlanCard
                name="Pro"
                price="$29"
                period="/month"
                description="For high-volume businesses"
features={[
  "Unlimited review requests",
  "Everything in Lifetime",
  "Priority email support",
  "First access to new features",
]}
                buttonText="Subscribe to Pro"
                buttonVariant="outline"
                onSelect={() => handleSelectPlan("pro")}
                isPending={checkoutMutation.isPending}
                testId="plan-pro"
              />
            </div>
          </div>
        </section>

        <FAQ />
      </main>
      <Footer />
    </div>
  );
}
